import type { Vec2 } from '../utils/vec2.js';

import type { MatchState } from './State.js';
import type { Building } from './entities/Building.js';
import type { Core } from './entities/Core.js';

// Build slots ring each Core at fixed offsets. Slot index 0 starts at the top
// (-π/2) and increases clockwise every 60°. Positions are derived from Core.pos
// at read time so they don't bloat MatchState — same Core position always yields
// the same slot positions, preserving Rule-5 serializability.
export const SLOT_RING_RADIUS_PX = 200;
export const SLOT_PICK_RADIUS_PX = 60;

const TWO_PI = Math.PI * 2;
const SLOT_ANGLE_OFFSET = -Math.PI / 2;

export function slotWorldPos(core: Core, slotIndex: number): Vec2 {
  const angle = SLOT_ANGLE_OFFSET + (slotIndex * TWO_PI) / 6;
  return {
    x: core.pos.x + Math.cos(angle) * SLOT_RING_RADIUS_PX,
    y: core.pos.y + Math.sin(angle) * SLOT_RING_RADIUS_PX,
  };
}

// True if a building (any state, including under-construction) already occupies
// the slot. BuildCommand validation rejects new builds on occupied slots.
// mini_turret is excluded — it's a free-standing ability spawn that doesn't bind
// to a Core slot.
export function isSlotOccupied(state: MatchState, coreId: number, slotIndex: number): boolean {
  for (const b of Object.values(state.buildings)) {
    if (b.kind === 'mini_turret') continue;
    if (b.coreId === coreId && b.slotIndex === slotIndex) return true;
  }
  return false;
}

// Picks the slot whose center is closest to (x,y) within SLOT_PICK_RADIUS_PX, on a
// Core owned by `ownerId`. Returns null if no slot is in range. Used by the client
// when build mode translates a click into a BuildCommand.
export function pickSlotAt(
  state: MatchState,
  ownerId: 0 | 1,
  x: number,
  y: number,
): { coreId: number; slotIndex: number } | null {
  let best: { coreId: number; slotIndex: number } | null = null;
  let bestSq = SLOT_PICK_RADIUS_PX * SLOT_PICK_RADIUS_PX;
  for (const core of Object.values(state.cores)) {
    if (core.ownerId !== ownerId) continue;
    for (let i = 0; i < 6; i++) {
      const p = slotWorldPos(core, i);
      const dSq = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
      if (dSq < bestSq) {
        bestSq = dSq;
        best = { coreId: core.id, slotIndex: i };
      }
    }
  }
  return best;
}

export function findCoreOwnedBy(state: MatchState, ownerId: 0 | 1): Core | null {
  for (const core of Object.values(state.cores)) {
    if (core.ownerId === ownerId) return core;
  }
  return null;
}

export function operationalHubsOf(state: MatchState, ownerId: 0 | 1): Building[] {
  const out: Building[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.ownerId !== ownerId) continue;
    if (b.kind !== 'production_hub') continue;
    if (b.buildProgress < 1) continue;
    out.push(b);
  }
  return out;
}
